import React, { useEffect, useState } from 'react';
import styles from './EditableDropdown.module.scss';
import bottomAngleIcon from '../../assets/bottomAngleIcon.svg';
import clsx from 'clsx';
import greenTick from '../../assets/greenTick.svg';
import { KEYBOARD_EVENTS } from '../../utils/constants/commonConstants';

function EditableDropdown({
    id,
    value,
    options,
    setOutput,
    removeInput,
    imgStyle,
    placeHolder,
    showTick,
    removeBorder,
    wrapperStyle,
    wrapperClassName,
    disabled,
    optionStyle,
    inputStyle,
    inputClassName,
    optionsObject,
    dropdownClassName,
    onAddNew,
    addNewLabel,
    deferCommit,
    readOnly = false,
}: {
    id: string;
    value?: string | number;
    options: string[];
    imgStyle?: any;
    wrapperStyle?: any;
    wrapperClassName?: string;
    showTick?: boolean;
    setOutput: Function;
    removeBorder?: boolean;
    removeInput?: boolean;
    placeHolder?: string;
    disabled?: boolean;
    optionStyle?: any;
    inputStyle?: any;
    inputClassName?: string;
    optionsObject?: any[];
    dropdownClassName?: string;
    onAddNew?: (typed: string) => void;
    addNewLabel?: (typed: string) => string;
    /** When true: typing only filters; setOutput fires only on pick, Add-new, or Enter. Blur reverts to last committed value. */
    deferCommit?: boolean;
    readOnly?: boolean;
}) {
    const [inputValue, setInputValue] = useState<number | string>('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        filterOptions(value);
        if (!deferCommit) setOutput(value);
    };
    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);
    useEffect(() => {
        setInputValue(value ?? '');
    }, [value]);

    const filterOptions = (value) => {
        const filtered = options.filter((option) => option.toLowerCase().includes(value.toLowerCase()));
        setFilteredOptions(filtered);
    };

    const handleOptionClick = (option) => {
        setInputValue(option);
        setOutput(option);
        setIsOpen(false);
    };

    const typed = String(inputValue);

    return (
        <div style={wrapperStyle} className={clsx(styles.Wrapper, wrapperClassName)}>
            <input
                id={`editableDropdown${id}`}
                style={
                    removeInput
                        ? disabled
                            ? {
                                  color: '#C8C8C8',
                                  caretColor: 'transparent',
                                  cursor: 'not-allowed',
                                  pointerEvents: 'none',
                                  width: '100%',
                                  ...inputStyle,
                              }
                            : {
                                  caretColor: 'transparent',
                                  cursor: 'pointer',
                                  width: '100%',
                                  ...inputStyle,
                              }
                        : { ...inputStyle }
                }
                type="text"
                readOnly={readOnly}
                value={inputValue}
                autoComplete={'off'}
                placeholder={placeHolder}
                className={clsx(removeBorder ? styles.InputNoB : styles.Input, inputClassName)}
                onChange={(e) => {
                    if (removeInput) return;
                    handleInputChange(e);
                }}
                onKeyDown={(e) => {
                    if (deferCommit && e.key === KEYBOARD_EVENTS.ENTER) {
                        setOutput(String(inputValue));
                        setIsOpen(false);
                    }
                }}
                onFocus={() => setIsOpen(true)}
                onBlur={() => {
                    if (deferCommit) setInputValue(value ?? '');
                    setTimeout(() => setIsOpen(false), 150);
                }}
            />
            <img
                style={imgStyle}
                className={clsx(styles.DropdownIcon, {
                    [styles.DropdownIconInverted]: isOpen,
                })}
                src={bottomAngleIcon}
                onClick={() => {
                    if (disabled) return;
                    document.getElementById(`editableDropdown${id}`)?.focus();
                }}
                alt="bottomAngleIcon"
                width="24px"
                height="24px"
            />

            <div
                style={
                    isOpen
                        ? { maxHeight: '200px', overflowY: 'auto' }
                        : { maxHeight: '0', overflowY: 'hidden', padding: 0, boxShadow: 'none', border: 'none' }
                }
                className={clsx(styles.Dropdown, dropdownClassName)}
            >
                {filteredOptions?.map((option: any) => (
                    <div
                        className={clsx({
                            [styles.OptionWithSubtext]: typeof option === 'object',
                            [styles.Option]: typeof option !== 'object',
                            [styles.Disabled]: optionsObject?.find((obj) => obj.misDisplayName === option)?.isDataAvailable === false,
                        })}
                        style={optionStyle}
                        key={option}
                        onClick={() => handleOptionClick(typeof option === 'object' ? option.cellRange : option)}
                    >
                        {showTick && <img src={greenTick} alt="tick" />}
                        {typeof option === 'object' ? option.cellRange : option}
                        {typeof option === 'object' && <span style={{ color: 'grey', fontSize: '0.8rem' }}>{option.cellDescription}</span>}
                    </div>
                ))}
                {onAddNew && typed !== '' && !options.includes(typed) && (
                    <div
                        className={styles.AddNewRow}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onAddNew(typed);
                            setIsOpen(false);
                        }}
                    >
                        {`⊕ ${addNewLabel ? addNewLabel(typed) : `Add "${typed}"`}`}
                    </div>
                )}
            </div>
        </div>
    );
}

export default EditableDropdown;
